/**
 * Onboarding Module Constants
 * 
 * Central location for all configuration values used throughout the onboarding system.
 */

export const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Kanban board stages - defines the candidate journey
 * Updated design with dot colors and column background colors
 */
export const STAGES = [
  { 
    id: 'interested', 
    label: 'Interested', 
    dotColor: 'bg-slate-400',
    countBg: 'bg-slate-200 dark:bg-[#3e4145]',
    countText: 'text-slate-700 dark:text-slate-200',
    columnBg: 'bg-slate-50 dark:bg-[#1e1f22]'
  },
  { 
    id: 'auditioned', 
    label: 'Auditioning', 
    dotColor: 'bg-amber-400',
    countBg: 'bg-amber-400',
    countText: 'text-white',
    columnBg: 'bg-amber-50/70 dark:bg-amber-500/10'
  },
  { 
    id: 'onboarding', 
    label: 'Onboarding', 
    dotColor: 'bg-blue-400',
    countBg: 'bg-blue-400',
    countText: 'text-white',
    columnBg: 'bg-blue-50/70 dark:bg-blue-500/10'
  },
  { 
    id: 'training', 
    label: 'Training', 
    dotColor: 'bg-pink-400',
    countBg: 'bg-pink-200 dark:bg-pink-500/30',
    countText: 'text-pink-700 dark:text-pink-200',
    columnBg: 'bg-pink-50/50 dark:bg-pink-500/10'
  },
  { 
    id: 'on_team', 
    label: 'On Team!', 
    dotColor: 'bg-emerald-400',
    countBg: 'bg-emerald-200 dark:bg-emerald-500/30',
    countText: 'text-emerald-700 dark:text-emerald-200',
    columnBg: 'bg-emerald-50/50 dark:bg-emerald-500/10'
  }
];

/**
 * Color mapping for candidate roles/instruments
 * Updated to match new pastel design
 */
export const ROLE_COLORS = {
  vocals: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-600 dark:text-blue-400', pill: true },
  band: { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-600 dark:text-rose-400', pill: true },
  'worship leader': { bg: 'bg-violet-100', text: 'text-violet-600 dark:text-violet-400', pill: true },
  'worship_leader': { bg: 'bg-violet-100', text: 'text-violet-600 dark:text-violet-400', pill: true },
  guitar: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-300', pill: true },
  bass: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300', pill: true },
  drums: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-300', pill: true },
  keys: { bg: 'bg-pink-100 dark:bg-pink-900/50', text: 'text-pink-700 dark:text-pink-300', pill: true },
  production: { bg: 'bg-cyan-100', text: 'text-cyan-700', pill: true },
  tech: { bg: 'bg-cyan-100 dark:bg-cyan-900/50', text: 'text-cyan-700 dark:text-cyan-300', pill: true },
  other: { bg: 'bg-slate-200 dark:bg-[#3e4145]', text: 'text-slate-600 dark:text-slate-300', pill: true }
};

/**
 * Deterministic color palette for custom areas of interest.
 * Used when the church has added a custom area (e.g. "Tech Booth", "Greeter")
 * that doesn't match a built-in ROLE_COLORS key. The hash mapping ensures the
 * same custom id always gets the same color across reloads.
 */
const CUSTOM_AREA_PALETTE = [
  { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300' },
  { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-300' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/50', text: 'text-emerald-700 dark:text-emerald-300' },
  { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-300' },
  { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-700 dark:text-rose-300' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/50', text: 'text-cyan-700 dark:text-cyan-300' },
  { bg: 'bg-pink-100 dark:bg-pink-900/50', text: 'text-pink-700 dark:text-pink-300' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/50', text: 'text-indigo-700 dark:text-indigo-300' },
];

/**
 * Resolve a candidate's stored area-of-interest id to its display label.
 * Falls back through:
 *   1. Church's customized areas_of_interest_options (settings) — current shape
 *   2. Title-cased snake_case rendering of the raw id (for legacy/unknown values)
 *
 * This decoupling means renaming a custom area's label keeps existing
 * candidates labeled correctly (we look up by the stable id) and removing
 * a custom area doesn't break the kanban view for candidates who had selected it.
 */
export function resolveAreaLabel(areaId, areasOptions) {
  if (!areaId) return '';
  const idLower = String(areaId).toLowerCase();
  if (Array.isArray(areasOptions)) {
    const match = areasOptions.find(o => String(o.id || o.value || '').toLowerCase() === idLower);
    if (match?.label) return match.label;
  }
  // Fallback: prettify the raw id (e.g. "tech_booth" → "Tech Booth")
  return idLower
    .split(/[_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * The 32-bit string hash both palettes use. Extracted so the Studio hue slot
 * (resolveAreaHue) and the classic pastel pick stay the same kind of stable:
 * the same id always lands on the same slot, across reloads and across every
 * surface the area appears on.
 */
function hashKey(s) {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0; // 32-bit
  }
  return Math.abs(hash);
}

/**
 * Resolve color styling for an area-of-interest pill.
 * 1. Use ROLE_COLORS map (built-in keys like 'vocals', 'band', 'tech')
 * 2. Deterministic hash → CUSTOM_AREA_PALETTE for stable colors on unknown ids
 */
export function resolveAreaColor(areaId) {
  const idLower = String(areaId || '').toLowerCase();
  const builtIn = ROLE_COLORS[idLower];
  if (builtIn) return builtIn;

  // Hash by id so the same custom option always gets the same palette entry.
  const pick = CUSTOM_AREA_PALETTE[hashKey(idLower) % CUSTOM_AREA_PALETTE.length];
  return { ...pick, pill: true };
}

/**
 * Studio only (doc 121 R3 — "colour as orientation"): map a chip's visible text
 * to one of the seven `--st-area-*` hue slots. Pure lookup, no behaviour: the
 * value is written to `data-area` on Studio chips so studio.css can tint them.
 * Classic never carries the attribute. Anything unrecognised → "other".
 *
 * The visible label is tried first (it is what the reader is looking at), then
 * the candidate's stored area id, so a church that renames "Keys" to
 * "Piano Team" keeps the keys hue instead of falling back to "other".
 */
const AREA_SLUG_ALIASES = {
  vocals: 'vocals', vocal: 'vocals', vocalist: 'vocals', vocalists: 'vocals',
  singer: 'vocals', singers: 'vocals',
  keys: 'keys', key: 'keys', keyboard: 'keys', keyboards: 'keys', piano: 'keys',
  drums: 'drums', drum: 'drums', drummer: 'drums', percussion: 'drums',
  band: 'band', guitar: 'band', guitars: 'band', guitarist: 'band',
  'electric guitar': 'band', 'acoustic guitar': 'band',
  tech: 'tech', technical: 'tech', 'tech booth': 'tech', production: 'tech',
  sound: 'tech', audio: 'tech', av: 'tech', media: 'tech', lights: 'tech',
  lighting: 'tech',
  bass: 'bass', bassist: 'bass', 'bass guitar': 'bass',
  other: 'other',
};

function areaSlugKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

export function resolveAreaSlug(text, areaId) {
  return AREA_SLUG_ALIASES[areaSlugKey(text)]
    || AREA_SLUG_ALIASES[areaSlugKey(areaId)]
    || 'other';
}

/**
 * Studio only (doc 121 §5 D5 — "area hues for every area, including
 * admin-added positions"). The seven named areas above keep their fixed
 * `--st-area-<name>` hue; every *other* area — "Worship Leader", a church's
 * custom "Greeter", anything an admin types into the intake form's options —
 * takes one of ten hue slots by hash of its stored id, so the same area is the
 * same colour everywhere a chip for it is drawn (today: the board card and the
 * candidate modal — the intake form lists its areas as checkbox rows, not
 * chips). Returns "h1".."h10", or undefined when the area is named (including
 * the literal "Other", which is a named area and keeps its grey).
 *
 * Written to `data-area-hue` next to `data-area`; studio.css §42 paints it.
 * Classic never carries the attribute.
 */
export const AREA_HUE_SLOTS = 10;

export function resolveAreaHue(text, areaId) {
  if (resolveAreaSlug(text, areaId) !== 'other') return undefined;
  // Prefer the stored id: a church can rename a custom area's label and the
  // colour must not move. Fall back to the visible text for legacy rows that
  // only ever carried a label.
  const key = areaSlugKey(areaId) || areaSlugKey(text);
  if (!key || key === 'other') return undefined;
  return 'h' + ((hashKey(key) % AREA_HUE_SLOTS) + 1);
}

/**
 * Training instrument options - used when moving candidate to Training stage
 */
export const TRAINING_INSTRUMENTS = [
  { value: 'vocalist', label: 'Vocalist' },
  { value: 'acoustic_guitar', label: 'Acoustic Guitar' },
  { value: 'electric_guitar', label: 'Electric Guitar' },
  { value: 'piano_keys', label: 'Piano/Keys' },
  { value: 'bass', label: 'Bass' },
  { value: 'drums', label: 'Drums' },
  { value: 'percussionist', label: 'Percussionist' },
  { value: 'music_director', label: 'Music Director' }
];

/**
 * Default form fields for the interest/intake form
 */
export const DEFAULT_FORM_FIELDS = [
  { id: 'experience', label: 'How long have you been playing/singing?', type: 'text', required: true },
  { id: 'church_member', label: 'Are you a member of this church?', type: 'select', options: ['Yes', 'No', 'Attending regularly'], required: true },
  { id: 'availability', label: 'What is your typical availability?', type: 'textarea', required: false }
];

/**
 * Onboarding document types
 */
export const ONBOARDING_DOC_TYPES = [
  { key: 'values_doc', title: 'Values Document', description: 'Share your team values' },
  { key: 'expectations_doc', title: 'Expectations', description: 'What you expect from team members' },
  { key: 'faq_doc', title: 'FAQ', description: 'Frequently asked questions' },
  { key: 'handbook_doc', title: 'Handbook', description: 'Team handbook and guidelines' }
];
